import { Button, FiltersIcon, Modal } from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigation } from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { FilterActions } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/filterSelector/filterActions';
import {
  useBloomFilters,
  useProductSearchFilters,
  useSearchParamFilters,
} from '@jane/bloom/hooks/filters';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import { getFiltersCount } from '@jane/bloom/utils/filters/getFiltersCount';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { getFacetRange } from '@jane/bloom/viewModels/facetRange';
import type { ProductSearchFiltersViewModel } from '@jane/bloom/viewModels/productSearchFilters';

import { ModalButtonFilterSection } from './allFiltersModal/buttonFilterSection';
import {
  ModalFilterEntrypoint,
  ModalFilterSection,
} from './allFiltersModal/modalFiltersSection';
import { PotencySection } from './allFiltersModal/potencySection';
import { RatingSection } from './allFiltersModal/ratingSection';
import { SortSection } from './allFiltersModal/sortSection';
import { JaneGoldSection } from './allFiltersModal/specialsSection';
import styles from './mobileFiltersModal.module.css';

interface FiltersButtonProps {
  availableProductsCount?: number;
  currentSort: SortOptions;
  hasJaneGoldProducts?: boolean;
  shouldShowGold?: boolean;
  sortOptions: LabeledOption<SortOptions>[];
}

export function MobileFiltersModal({
  availableProductsCount,
  currentSort,
  hasJaneGoldProducts,
  shouldShowGold,
  sortOptions,
}: FiltersButtonProps) {
  const {
    getPotencyFilterKeysNonEmpty,
    getProductSearchFilterOptions,
    productSearchFilters,
  } = useProductSearchFilters();
  const { sortedAllDisplayFilterKey } = useBloomFilters();
  const { clearAllFilters, searchParams, setSearchParams } =
    useSearchParamFilters();
  const [resetting, setResetting] = useState(false);
  const { state } = useNavigation();

  const [originalFilters, setOriginalFilters] = useState({});

  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('default');

  const validPotencySliderKeys = getPotencyFilterKeysNonEmpty().filter(
    (key) => {
      const filterList = getProductSearchFilterOptions(key);
      const { rangeMax, rangeMin } = getFacetRange(filterList);
      return rangeMax > rangeMin;
    }
  );

  const activeFiltersCount = useMemo(
    () => getFiltersCount({ searchParams, sortOptions }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams]
  );

  const filterBtnLabel = `Filter ${activeFiltersCount === 0 ? '' : `(${activeFiltersCount})`}`;

  const saleItem = getProductSearchFilterOptions('categories').find(
    (option) => option.value === 'sale'
  );

  const sections = sortedAllDisplayFilterKey
    .filter(
      (filterKey) =>
        ['ratings', 'potencies', 'prices', 'weights', 'specials'].includes(
          filterKey
        ) ||
        productSearchFilters?.[filterKey as keyof ProductSearchFiltersViewModel]
          ?.length ||
        -1 > 0
    )
    .map((filterKey) => {
      switch (filterKey) {
        case 'ratings': {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <RatingSection key={filterKey} />
            )
          );
        }
        case 'potencies': {
          return (
            validPotencySliderKeys.length > 0 && (
              <PotencySection key={filterKey} resetting={resetting} />
            )
          );
        }
        case 'prices': {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <ModalButtonFilterSection key={filterKey} filterKey={filterKey} />
            )
          );
        }
        case 'brands': {
          return (
            !isEmpty(productSearchFilters?.['brands']) && (
              <ModalFilterEntrypoint
                key={filterKey}
                filterKey={filterKey}
                handleClick={() => setSelectedTab('brands')}
              />
            )
          );
        }
        case 'terpenes': {
          return (
            !isEmpty(productSearchFilters?.['terpenes']) && (
              <ModalFilterEntrypoint
                key={filterKey}
                filterKey={filterKey}
                handleClick={() => setSelectedTab('terpenes')}
              />
            )
          );
        }
        case 'jane-gold': {
          return false;
        }
        default: {
          return (
            getProductSearchFilterOptions(
              filterKey as keyof ProductSearchFiltersViewModel
            ).length > 0 && (
              <ModalButtonFilterSection
                key={filterKey}
                filterKey={filterKey}
                icon
                shouldShowDividerBelow={
                  sortedAllDisplayFilterKey[
                    sortedAllDisplayFilterKey.length - 1
                  ] !== filterKey
                }
              />
            )
          );
        }
      }
    });

  useEffect(() => {
    if (resetting && state === 'idle') {
      setResetting(false);
    }
  }, [resetting, state]);

  const renderView = (isMobile: boolean) => {
    switch (selectedTab) {
      case 'brands':
        return (
          <ModalFilterSection selectedTab={selectedTab} resetting={resetting} />
        );
      case 'terpenes':
        return (
          <ModalFilterSection selectedTab={selectedTab} resetting={resetting} />
        );
      default:
        return (
          <>
            <Modal.Header
              title="Filters and sort"
              textAlign="center"
              branded={false}
            />
            <Modal.Content>
              <SortSection
                isMobile={isMobile}
                sortOptions={sortOptions}
                currentSort={currentSort}
              />
              {shouldShowGold && hasJaneGoldProducts && saleItem && (
                <JaneGoldSection
                  saleItem={saleItem}
                  hasJaneGoldProducts={hasJaneGoldProducts}
                />
              )}
              {sections}
            </Modal.Content>
          </>
        );
    }
  };

  return (
    <div className={styles['mobileFiltersButton']}>
      <Button
        variant="tertiary"
        endIcon={<FiltersIcon />}
        label={filterBtnLabel}
        key="filterAndSort"
        ariaLabel="Filter and Sort"
        onClick={() => {
          setOriginalFilters(searchParams);
          setOpen(true);
        }}
      />
      <ClientOnly>
        {(isMobile) => (
          <Modal
            background="grays-white"
            open={open}
            onRequestClose={
              selectedTab === 'default'
                ? () => {
                    setOpen(false);
                    setSearchParams(originalFilters);
                  }
                : () => setSelectedTab('default')
            }
            className={styles['mobileFiltersModal']}
            variant="full-screen"
            overlayClose
          >
            {renderView(isMobile)}
            <Modal.Footer>
              <FilterActions
                availableProductsCount={availableProductsCount}
                fullButtons={{ show: true }}
                handleClearAll={clearAllFilters}
                handleClose={() => setOpen(false)}
              />
            </Modal.Footer>
          </Modal>
        )}
      </ClientOnly>
    </div>
  );
}
